import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useBlacklistCustomerMutation } from '@/api/endpoints';
import type { Customer } from '@/api/types';
import { Button } from '@/design/Button';
import { Field } from '@/design/Field';
import { Modal } from '@/design/Modal';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';

/**
 * Refusing somebody further bookings.
 *
 * The reason is required, and not as paperwork. Six months on, the customer
 * will argue, and the owner has to be able to say what happened without
 * relying on anybody's memory — so the box cannot be skipped, and it says what
 * a useful answer looks like rather than just "Reason".
 *
 * The modal closes on the way out and the list refreshes behind it, which is
 * what "land back where you started" means for a screen like this.
 */

interface Props {
  readonly customer: Customer | null;
  readonly onClose: () => void;
}

export function BlacklistModal({ customer, onClose }: Props): ReactElement {
  const [reason, setReason] = useState('');
  const [blacklist, saving] = useBlacklistCustomerMutation();
  const toast = useToast();
  const problems = readApiError(saving.error);

  // Cleared when a different customer is opened, so last time's reason is
  // never sitting in the box waiting to be attached to the wrong person.
  useEffect(() => setReason(''), [customer?.id]);

  async function save(): Promise<void> {
    if (customer === null) return;
    try {
      await blacklist({ id: customer.id, reason }).unwrap();
      toast.done(`${customer.name} is blacklisted. No further bookings will be taken.`);
      onClose();
    } catch {
      // The message stays in the modal: it is about what was typed in it.
    }
  }

  const tooShort = reason.trim().length < 8;

  return (
    <Modal
      open={customer !== null}
      onClose={onClose}
      title={customer === null ? 'Blacklist' : `Blacklist ${customer.name}`}
      note="They keep their history. The counter stops being able to book them."
      footer={
        <>
          <Button
            intent="danger"
            size="touch"
            type="submit"
            form="blacklist-form"
            disabled={saving.isLoading || tooShort}
          >
            {saving.isLoading ? 'Saving' : 'Blacklist them'}
          </Button>
          <Button size="touch" onClick={onClose}>
            Cancel
          </Button>
          {tooShort && (
            <span className="text-small text-ink-muted">
              Write a sentence first — it is what the next person will read.
            </span>
          )}
        </>
      }
    >
      <form
        id="blacklist-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        className="flex flex-col gap-4"
      >
        <Field
          label="Why"
          required
          error={problems.fields['reason'] ?? problems.detail}
        >
          {({ id, describedBy }) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              rows={3}
              autoFocus
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Returned BK-26-0014 three days late and would not pay the extra days."
              className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
            />
          )}
        </Field>

        <p className="border-l-[3px] border-l-attention bg-attention-wash px-3 py-2 text-small">
          Anybody can take them off the list again later. The reason is kept either way.
        </p>
      </form>
    </Modal>
  );
}
